// Wind against tide at curated downwind runs, for safety policy v2 (accelerated rollout Phase D,
// 1 October 2026; docs/handover/phase-2c.md, "Accelerated rollout, Phase D (1 October 2026)").
// One rule for downwind foil and the downwind paddle profile, in the engines' per-hour modules, the
// named paddle transport and the interim day summary the surf slice publishes at S2.
//
// Why: a downwind run is ridden in the stream itself, along its curated route. Where the stream
// sets against the wind, the wind sea it carries steepens: an opposing current shortens the waves
// and concentrates their energy (wave action is conserved, Longuet-Higgins and Stewart 1961), until
// at a quarter of the deep-water phase speed the shortest waves are blocked and break. The bumps a
// run rides are short wind sea (Open-Meteo's wind-wave period has a median of 3.6 s over the opposed
// hours at the 19 curated runs, where blocking starts at 1.4 m/s), so a stream of 0.5 m/s against
// them raises their steepness about 1.5 times and one of 0.75 m/s about 2 times. A wind sea at 15 kn
// is already near whitecapping, so doubling its steepness gives breaking seas, and a rider or
// paddler who falls is held in the stream that built them. Seamanship guidance names the same
// hazard: a moderate breeze against a spring stream reads like a stronger wind. No v2 row judged it
// before: the current rows read the stream's speed, the sea-state rows the model sea, and neither
// sees the two against each other.
//
// The rule (WIND_AGAINST_TIDE_V2):
// - it applies only on a curated run (a route bearing is known); elsewhere the current cell is not
//   necessarily the water anyone rides, and the speed-only current rows stand;
// - the stream opposes the wind when it sets within 45 degrees of straight into the wind (its
//   "towards" bearing within 45 degrees of the wind's "from" bearing);
// - red: a stream of 0.75 m/s (1.5 kn) or more opposing a wind of 15 kn (28 km/h) or more;
// - amber: a stream of 0.5 m/s (1 kn) or more opposing a wind of 12 kn (22 km/h, Beaufort 4) or more.
//
// Missing inputs never read as safer (design 2.5): a missing stream direction counts as opposed (as
// the seaward rule counts a missing direction as seaward), and so does a missing wind direction. It
// once took the run's own wind (the route's reciprocal), but a run can be ridden both ways on its one
// curated bearing (tarifa, porthleven, folly-beach and narrabeen have a reverse run, A-09), and on a
// reverse-run day a stream setting along the route is straight into the real wind, so the assumed
// wind read it clear (review of the integrated Phase D tree, 1 October 2026). A missing wind speed
// reads "unavailable" while the stream could qualify, unless the gust (an upper bound on the
// sustained wind) rules the amber wind out. A missing current adds no
// row (owner decision O-07: the current is an optional safety input). Directions follow
// seaward-current.ts: the stream's `towardsDeg` is where it flows TO (Open-Meteo's
// ocean_current_direction), the wind's `fromDeg` where it blows FROM.
//
// Pure and dependency-free: the route's interim rows (forecast-safety.ts, a declaration-only
// importer of the engines) and the engines' safety modules import it alike.

const KMH_PER_KNOT = 1.852;
/** Floating-point tolerance for knot thresholds (km/h to knots rounding), in knots. */
const KNOT_EPSILON = 1e-9;
/** Floating-point tolerance for the stream and angle thresholds. */
const EPSILON = 1e-9;

/** The wind-against-tide thresholds (see the header). */
export const WIND_AGAINST_TIDE_V2 = Object.freeze({
  /** The stream opposes the wind when it sets within this many degrees of straight into it. */
  opposedWithinDeg: 45,
  amber: Object.freeze({ streamAtLeastMs: 0.5, windAtLeastKn: 12 }),
  red: Object.freeze({ streamAtLeastMs: 0.75, windAtLeastKn: 15 }),
});

export type WindAgainstTideInput = Readonly<{
  /** The curated run's route bearing in degrees; null off a curated run (no row). */
  routeBearingDeg: number | null;
  /** The hour's sustained wind in km/h; null when missing. */
  windKmh: number | null;
  /** Where the wind blows from, in degrees; null when missing (counted as opposed). */
  windFromDeg: number | null;
  /** The hour's gust in km/h, an upper bound on a missing sustained wind; null when missing. */
  gustKmh: number | null;
  /** The same hour's stream speed in m/s; null when missing (no row, owner decision O-07). */
  streamMs: number | null;
  /** Where the stream flows to, in degrees; null when missing (counted as opposed). */
  streamTowardsDeg: number | null;
}>;

export type WindAgainstTideResult = Readonly<{
  level: "red" | "amber" | "unavailable" | "clear";
  /** The angle between the stream's set and straight into the wind (0 directly opposed); null when either direction is missing. */
  opposedAngleDeg: number | null;
  /** Whether the wind's direction was missing (counted as opposed). */
  windDirectionUnknown: boolean;
  /** Whether the stream's direction was missing (counted as opposed). */
  streamDirectionUnknown: boolean;
}>;

const CLEAR: WindAgainstTideResult = Object.freeze({ level: "clear", opposedAngleDeg: null, windDirectionUnknown: false, streamDirectionUnknown: false });

const finite = (value: number | null | undefined): value is number => typeof value === "number" && Number.isFinite(value);

/** The smallest angle between two bearings, in degrees (0 to 180). */
export function bearingDifferenceDeg(a: number, b: number): number {
  const difference = Math.abs((((a - b) % 360) + 360) % 360);
  return Math.min(difference, 360 - difference);
}

function atLeastKnots(speedKmh: number, knots: number): boolean {
  return speedKmh / KMH_PER_KNOT >= knots - KNOT_EPSILON;
}

/** The wind-against-tide level of one hour at a curated run (see the header). */
export function classifyWindAgainstTide(input: WindAgainstTideInput): WindAgainstTideResult {
  const rule = WIND_AGAINST_TIDE_V2;
  if (!finite(input.routeBearingDeg)) return CLEAR;
  const stream = input.streamMs;
  if (!finite(stream) || stream < 0 || stream < rule.amber.streamAtLeastMs - EPSILON) return CLEAR;

  // Either direction missing: the stream is counted as opposed (see the header).
  const windDirectionUnknown = !finite(input.windFromDeg);
  const streamDirectionUnknown = !finite(input.streamTowardsDeg);
  const angle = windDirectionUnknown || streamDirectionUnknown
    ? null
    : bearingDifferenceDeg(input.streamTowardsDeg as number, input.windFromDeg as number);
  if (angle !== null && angle > rule.opposedWithinDeg + EPSILON) return CLEAR;
  const base = { opposedAngleDeg: angle, windDirectionUnknown, streamDirectionUnknown };

  const wind = finite(input.windKmh) && input.windKmh >= 0 ? input.windKmh : null;
  if (wind === null) {
    // The sustained wind never exceeds its gust, so a gust below the amber wind rules the row out.
    const gust = finite(input.gustKmh) && input.gustKmh >= 0 ? input.gustKmh : null;
    if (gust !== null && !atLeastKnots(gust, rule.amber.windAtLeastKn)) return CLEAR;
    return { ...base, level: "unavailable" };
  }
  if (stream >= rule.red.streamAtLeastMs - EPSILON && atLeastKnots(wind, rule.red.windAtLeastKn)) return { ...base, level: "red" };
  if (atLeastKnots(wind, rule.amber.windAtLeastKn)) return { ...base, level: "amber" };
  return CLEAR;
}

/** The detail sentence of a red or amber wind-against-tide row (the rows' shared wording). */
export function windAgainstTideDetail(result: WindAgainstTideResult, windKmh: number | null, streamMs: number): string {
  const stream = `${streamMs.toFixed(1)} m/s`;
  const wind = windKmh === null ? "the wind" : `a ${Math.round(windKmh)} km/h wind`;
  const against = result.streamDirectionUnknown
    ? `A ${stream} stream of unknown direction may be setting against ${wind}`
    : result.windDirectionUnknown
      ? `A ${stream} stream may be setting against ${wind} of unknown direction`
      : `A ${stream} stream is setting against ${wind}`;
  return result.level === "red"
    ? `${against}. The bumps steepen and break; a fall leaves you in the stream. Run at slack water or with the tide.`
    : `${against}. Expect steeper, shorter bumps; plan the run for slack water or with the tide.`;
}
